import React, { useState, useEffect } from "react"
import { useForm, useWatch } from "react-hook-form"
import Table from "../../common/Table"
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline"
import AyudaFilter from "../cards/AyudaFilter"
import { ayudaApi } from "../../../services/api"
import { RegionalDropdown } from "./RegionalDropdown"
import toast from "react-hot-toast"

export const ProgramCreation = ({
  initialState = "0",
  selectedAyuda,
  resetProgramForm,
  onSuccess,
  initialSelectedResidents = [],
}) => {
  const { register, control, setValue, handleSubmit, watch } = useForm({
    defaultValues: {
      regions: selectedAyuda?.coverage?.region || [],
      provinces: selectedAyuda?.coverage?.province || [],
      municipalities: selectedAyuda?.coverage?.municipality || [],
      barangays: selectedAyuda?.coverage?.barangay || [],
      title: selectedAyuda?.title || "",
      description: selectedAyuda?.description || "",
      amount: selectedAyuda?.amount || "",
      national: selectedAyuda?.is_national || false,
      is_per_household: String(selectedAyuda?.is_per_household ?? 0),
    },
  })

  const isPerHousehold = useWatch({ control, name: "is_per_household" })
  const isNational = useWatch({ control, name: "national" })
  const selectedRegions = watch("regions") || []
  const selectedProvinces = watch("provinces") || []
  const selectedMunicipalities = watch("municipalities") || []
  const selectedBarangays = watch("barangays") || []

  const [regions, setRegions] = useState([])
  const [provinces, setProvinces] = useState([])
  const [municipalities, setMunicipalities] = useState([])
  const [barangays, setBarangays] = useState([])
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(initialState)
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedRows, setSelectedRows] = useState(initialSelectedResidents)
  const [filters, setFilters] = useState({
    gender: "all",
    civil_status: "all",
    vulnerable_sector: "all",
    age: "all",
    monthly_income: "all",
    search: "",
  })

  const [originalResidentIds] = useState(
    initialSelectedResidents.map((r) => r.id ?? r)
  )
  const selectedResidentIds = selectedRows.map((r) => r.id ?? r)
  const deleted_ids = selectedAyuda
    ? originalResidentIds.filter((id) => !selectedResidentIds.includes(id))
    : []

  const [residentsData, setResidentsData] = useState({
    residents: [],
    meta: { current_page: 1, last_page: 1, per_page: 10, total: 0 },
  })
  const [residentsLoading, setResidentsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const perPage = 10

  const residentColumns = [
    {
      key: "name",
      label: "Name",
      render: (_, row) => (
        <div className='font-medium text-gray-900'>{row.name}</div>
      ),
    },
    { key: "province", label: "Province", render: (value) => value || "—" },
    {
      key: "municipality",
      label: "Municipality",
      render: (value) => value || "—",
    },
    { key: "barangay", label: "Barangay", render: (value) => value || "—" },
    {
      key: "status",
      label: "Status",
      render: (status = "pending") => {
        const statusStyles = {
          released: "bg-green-100 text-green-700",
          approved: "bg-blue-100 text-blue-700",
          pending: "bg-yellow-100 text-yellow-700",
          rejected: "bg-red-100 text-red-700",
        }
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
              statusStyles[status] || "bg-gray-100 text-gray-700"
            }`}
          >
            {status}
          </span>
        )
      },
    },
  ]

  const fetchRegions = async () => {
    setLoading(true)
    try {
      const res = await fetch("https://psgc.gitlab.io/api/regions/")
      setRegions(await res.json())
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const fetchProvincesForRegions = async (regionCodes) => {
    setLoading(true)
    try {
      const allProvinces = await Promise.all(
        regionCodes.map(async (code) => {
          const res = await fetch(
            `https://psgc.gitlab.io/api/regions/${code}/provinces/`
          )
          return res.json()
        })
      )
      setProvinces(allProvinces.flat())
    } catch {
      setProvinces([])
    } finally {
      setLoading(false)
    }
  }

  const fetchMunicipalitiesForProvinces = async (provinceCodes) => {
    setLoading(true)
    try {
      const allMunicipalities = await Promise.all(
        provinceCodes.map(async (code) => {
          const res = await fetch(
            `https://psgc.gitlab.io/api/provinces/${code}/cities-municipalities/`
          )
          return res.json()
        })
      )
      setMunicipalities(allMunicipalities.flat())
    } catch {
      setMunicipalities([])
    } finally {
      setLoading(false)
    }
  }

  const fetchBarangaysForMunicipalities = async (municipalityCodes) => {
    setLoading(true)
    try {
      const allBarangays = await Promise.all(
        municipalityCodes.map(async (code) => {
          const res = await fetch(
            `https://psgc.gitlab.io/api/cities-municipalities/${code}/barangays/`
          )
          return res.json()
        })
      )
      setBarangays(allBarangays.flat())
    } catch {
      setBarangays([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!selectedAyuda?.coverage) return
    const {
      region = [],
      province = [],
      municipality = [],
    } = selectedAyuda.coverage
    if (region?.length) fetchProvincesForRegions(region)
    if (province?.length) fetchMunicipalitiesForProvinces(province)
    if (municipality?.length) fetchBarangaysForMunicipalities(municipality)
  }, [selectedAyuda])

  useEffect(() => {
    fetchRegions()
  }, [])

  useEffect(() => {
    if (selectedRegions.length > 0) {
      fetchProvincesForRegions(selectedRegions)
    } else {
      setProvinces([])
      setValue("provinces", [])
      setValue("municipalities", [])
      setValue("barangays", [])
    }
  }, [selectedRegions])

  useEffect(() => {
    if (selectedProvinces.length > 0) {
      fetchMunicipalitiesForProvinces(selectedProvinces)
    } else {
      setMunicipalities([])
      setValue("municipalities", [])
      setValue("barangays", [])
    }
  }, [selectedProvinces])

  useEffect(() => {
    if (selectedMunicipalities.length > 0) {
      fetchBarangaysForMunicipalities(selectedMunicipalities)
    } else {
      setBarangays([])
      setValue("barangays", [])
    }
  }, [selectedMunicipalities])

  const toggleSelection = (field, value) => {
    const current = watch(field) || []
    const newValue = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    setValue(field, newValue)
  }

  const removeItem = (field, value) => {
    const current = watch(field) || []
    setValue(
      field,
      current.filter((v) => v !== value)
    )
  }

  const getNameByCode = (list, code) => {
    const item = list.find((i) => i.code === code)
    return item?.name || code
  }

  const onSubmit = async (formData) => {
    try {
      setLoading(true)

      const buildCoverage = (codes, list) =>
        codes.map((code) => ({ code, name: getNameByCode(list, code) }))

      const payload = {
        title: formData.title,
        description: formData.description,
        amount: Number(formData.amount.replace(/,/g, "")),
        is_national: !!formData.national,
        is_per_household: Number(formData.is_per_household),
        monthly_income: filters.monthly_income || null,
        gender: filters.gender || null,
        civil_status: filters.civil_status || null,
        age: filters.age || null,
        vulnerable_sector: filters.vulnerable_sector || null,
        coverage: formData.national
          ? { region: [], province: [], municipality: [], barangay: [] }
          : {
              region: buildCoverage(formData.regions || [], regions),
              province: buildCoverage(formData.provinces || [], provinces),
              municipality: buildCoverage(
                formData.municipalities || [],
                municipalities
              ),
              barangay: buildCoverage(formData.barangays || [], barangays),
            },
        resident_ids: selectedResidentIds,
        deleted_ids,
      }

      if (selectedAyuda) {
        await ayudaApi.updateAyuda(selectedAyuda.id, payload)
      } else {
        await ayudaApi.createAyuda(payload)
      }

      if (onSuccess) onSuccess()
      toast.success("Ayuda program saved successfully")
      resetProgramForm()
    } catch (error) {
      console.error("Ayuda API failed:", error.response || error)
      toast.error("Failed to save Ayuda program")
    } finally {
      setLoading(false)
    }
  }

  const fetchResidents = async () => {
    setResidentsLoading(true)
    try {
      const params = {
        region: isNational ? [] : selectedRegions,
        province: isNational ? [] : selectedProvinces,
        municipality: isNational ? [] : selectedMunicipalities,
        barangay: isNational ? [] : selectedBarangays,
        is_per_household: Number(isPerHousehold),
        gender: filters.gender || "",
        civil_status: filters.civil_status || "",
        vulnerable_sector: filters.vulnerable_sector || "",
        age: filters.age || "",
        monthly_income: filters.monthly_income || "",
        search: filters.search || "",
        page,
        per_page: perPage,
      }

      const res = await ayudaApi.getAyudaFilterResidents(params)
      setResidentsData({
        residents: res.data?.data?.residents || [],
        meta: res.data?.data?.meta || {
          current_page: 1,
          last_page: 1,
          per_page: 10,
          total: 0,
        },
      })
    } catch (err) {
      console.error("Fetch residents failed:", err.response || err)
    } finally {
      setResidentsLoading(false)
    }
  }

  useEffect(() => {
    if (currentStep === 2) {
      fetchResidents()
    }
  }, [
    currentStep,
    page,
    filters,
    selectedRegions,
    selectedProvinces,
    selectedMunicipalities,
    selectedBarangays,
    isNational,
  ])

  const tableData = Array.isArray(residentsData.residents)
    ? residentsData.residents.map((r) => ({
        id: r.id,
        resident_id: r.id,
        name: [r.first_name, r.middle_name, r.last_name]
          .filter(Boolean)
          .join(" "),
        province: r.province ?? "—",
        municipality: r.municipality ?? "—",
        barangay: r.barangay ?? "—",
        status: r.status ?? "pending",
        raw: r,
      }))
    : []

  const handleFilterChange = (key, value) => {
    setPage(1)
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm'>
      <div className='w-full max-w-5xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col'>
        <div className='p-4 border-b flex items-center rounded-t-2xl justify-between sticky top-0 bg-white z-10'>
          <h3 className='text-2xl font-semibold'>
            {selectedAyuda
              ? `Edit ${selectedAyuda?.title} Program`
              : "New Ayuda Program"}
          </h3>
          <button
            onClick={resetProgramForm}
            className='text-gray-400 hover:text-gray-600 text-3xl'
          >
            ×
          </button>
        </div>

        <div className='flex-1 overflow-y-auto p-6 space-y-6 pb-2'>
          {currentStep === 1 && (
            <>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7'>
                <div className='group'>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Title *
                  </label>
                  <input
                    type='text'
                    {...register("title", { required: "Title is required" })}
                    className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300'
                  />
                </div>
                <div className='group'>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Description
                  </label>
                  <input
                    type='text'
                    {...register("description")}
                    className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300'
                  />
                </div>
              </div>

              <div className='flex items-center gap-3'>
                <input
                  type='checkbox'
                  {...register("national")}
                  className='h-5 w-5 rounded border-gray-300 text-blue-600 cursor-pointer focus:ring-blue-500'
                />
                <span className='text-sm font-medium text-gray-700'>
                  National Program
                </span>
              </div>

              {!isNational && (
                <RegionalDropdown
                  getNameByCode={getNameByCode}
                  selectedBarangays={selectedBarangays}
                  selectedProvinces={selectedProvinces}
                  selectedMunicipalities={selectedMunicipalities}
                  selectedRegions={selectedRegions}
                  toggleSelection={toggleSelection}
                  regions={regions}
                  provinces={provinces}
                  barangays={barangays}
                  municipalities={municipalities}
                  removeItem={removeItem}
                />
              )}

              <div className='flex-col grid grid-cols-2 gap-9'>
                <div className='group'>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Amount To Receive *
                  </label>
                  <input
                    type='text'
                    {...register("amount", { required: "Amount is required" })}
                    className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300'
                  />
                </div>

                <div className='flex gap-6 items-center'>
                  <label className='flex items-center gap-2 cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={Number(isPerHousehold) === 0}
                      onChange={() => setValue("is_per_household", 0)}
                      className='h-4 w-4'
                    />
                    Per Individual
                  </label>

                  <label className='flex items-center gap-2 cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={Number(isPerHousehold) === 1}
                      onChange={() => setValue("is_per_household", 1)}
                      className='h-4 w-4'
                    />
                    Per Household
                  </label>
                </div>
              </div>

              <AyudaFilter filters={filters} setFilters={setFilters} />
            </>
          )}

          {currentStep === 2 && (
            <>
              <div className='items-center py-2 flex justify-between border-b border-gray-200'>
                <h3 className='text-lg font-medium text-gray-900'>
                  Ayuda Recipients
                </h3>
                <div>
                  <input
                    type='text'
                    placeholder='Search recipients...'
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                    className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500'
                  />
                </div>
              </div>

              <Table
                columns={residentColumns}
                data={tableData}
                selectable
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                pagination={{
                  currentPage: residentsData.meta.current_page,
                  lastPage: residentsData.meta.last_page,
                  perPage: residentsData.meta.per_page,
                  total: residentsData.meta.total,
                }}
                onPageChange={setPage}
              />
            </>
          )}
        </div>

        <div className='sticky bottom-0 bg-white border-t p-4 rounded-b-2xl'>
          <div className='flex justify-between'>
            {currentStep === 2 ? (
              <button
                onClick={() => setCurrentStep(1)}
                className='px-6 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition flex items-center gap-2'
              >
                <ArrowLeftIcon className='w-4 h-4' /> Back
              </button>
            ) : (
              <div />
            )}

            <div className='flex gap-3'>
              <button
                onClick={resetProgramForm}
                className='px-6 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition'
              >
                Cancel
              </button>

              {currentStep === 1 ? (
                <button
                  onClick={() => setCurrentStep(2)}
                  className='px-6 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 transition disabled:bg-gray-400'
                >
                  Next <ArrowRightIcon className='w-4 h-4' />
                </button>
              ) : (
                <button
                  onClick={handleSubmit(onSubmit)}
                  className='px-6 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition disabled:bg-gray-400'
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Approve"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProgramCreation
